import React, { useRef } from "react";
import { Button, styled, Stack, buttonClasses } from "@mui/material";
import ChevronDown from "src/icons/ChevronDown";
import CloseIcon from "src/icons/CloseIcon";
import { useTheme } from "@mui/material/styles";
import FilterField from "@hyro-components/hyro-filter/components/filter-field/FilterField";
import HyroTruncateTypography from "@hyro-components/hyro-truncate-typography/HyroTruncateTypography";
import { FIELD_TYPES } from "@hyro-components/fields/utils";
import useFilterItem from "./FilterItem.hook";

const FilterItem = ({
  testId,
  id,
  label,
  value,
  isActive,
  type,
  disabled,
  onRemove,
  options,
  onClose,
  onClick,
  onChange,
  operator,
  isDefault = false
}) => {
  const theme = useTheme();
  const buttonRef = useRef(null);
  const { displayValue, tooltipText, hasValue } = useFilterItem({ value, type, label });

  return (
    <>
      <StyledButton
        ref={buttonRef}
        disabled={disabled}
        isActive={isActive}
        large={isDefault}
        onClick={onClick}
        data-filter-key={id}
        data-testid={testId}
      >
        <StyledTruncatedTypography
          variant={isDefault ? "tableHeader1" : "tableHeader2"}
          maxWidth={isDefault || type === FIELD_TYPES.DATE ? "none" : "150px"}
          disabled={disabled}
          hasValue={hasValue}
          isActive={isActive}
          large={isDefault}
          tooltipText={tooltipText}
          forceTooltip={type === FIELD_TYPES.DATE}
        >
          {hasValue ? `${label}: ${displayValue}` : label}
        </StyledTruncatedTypography>

        <ChevronDown
          stroke={disabled ? theme.palette.system[600] : isActive ? theme.palette.primary[500] : theme.palette.text[700]}
          strokeWidth={1.6}
          strokeOpacity={1}
          style={{
            width: isDefault ? 20 : 16,
            height: isDefault ? 20 : 16,
            transform: `rotate(${isActive ? 180 : 0}deg)`
          }}
        />

        {!isDefault && (
          <StyledCloseButton isActive={isActive} data-testid={`${testId}-remove-button`} onClick={onRemove}>
            <CloseIcon
              strokeWidth={1.6}
              color={disabled ? theme.palette.system[600] : isActive ? theme.palette.primary[500] : theme.palette.text[700]}
            />
          </StyledCloseButton>
        )}
      </StyledButton>

      <FilterField
        type={type}
        label={label}
        value={value}
        onChange={onChange}
        anchorEl={buttonRef.current}
        open={isActive}
        onClose={onClose}
        options={options}
        operator={operator}
      />
    </>
  );
};

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "isActive" && prop !== "large"
})(({ theme, isActive, large }) => ({
  [`&.${buttonClasses.root}`]: {
    gap: theme.spacing(0.5),
    padding: large ? theme.spacing(1, 1.5) : theme.spacing(0.375, 1.125, 0.375, 1.5),
    ...(large && { maxWidth: 230 }),
    backgroundColor: theme.palette.system[0],
    color: theme.palette.text[700],
    boxShadow: "none",
    border: `1px solid ${theme.palette.system[400]}`,
    ...(isActive && {
      backgroundColor: theme.palette.tertiary[300],
      color: theme.palette.primary[500],
      borderColor: "transparent"
    }),
    ...(!isActive && {
      "&:hover": {
        backgroundColor: theme.palette.system[50],
        boxShadow: "none"
      }
    })
  }
}));

const StyledTruncatedTypography = styled(HyroTruncateTypography, {
  shouldForwardProp: (prop) => prop !== "isActive" && prop !== "hasValue" && prop !== "disabled" && prop !== "large"
})(({ theme, hasValue, isActive, disabled, large }) => ({
  marginRight: theme.spacing(0.5),
  ...(large && { fontSize: 14, fontWeight: 500, lineHeight: "20px", letterSpacing: "0.4px" }),
  color: disabled ? theme.palette.system[600] :
    isActive ? theme.palette.primary[500] :
      large ? theme.palette.text.primary :
        hasValue ? theme.palette.text[800] : theme.palette.text[700]
}));

const StyledCloseButton = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "isActive"
})(({ theme, isActive }) => ({
  alignItems: "center",
  justifyContent: "center",
  width: 20,
  height: 20,
  borderRadius: "50%",
  padding: 4,
  transition: theme.transitions.create(["background-color", "transform"]),
  "& svg": {
    width: 16,
    height: 16
  },
  "&:hover": {
    transform: "scale(0.9)",
    backgroundColor: isActive ? theme.palette.primary[500] : theme.palette.system[700],
    "& svg path": {
      stroke: theme.palette.system[0],
      transition: theme.transitions.create(["stroke"])
    }
  }
}));

export default FilterItem;
